import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MultiSelect } from "../components/multi-select";
import { useBulkEnrollMembers, usePartners } from "../hooks/use-partner";
import { axiosInstance } from "../lib/axios";
import { Loader2 } from "lucide-react";

export default function BulkEnrollModal({ onClose }) {
  const [partnerId, setPartnerId] = useState("");
  const [membershipTypeId, setMembershipTypeId] = useState("");
  const [courseIds, setCourseIds] = useState([]);
  const [file, setFile] = useState(null);

  // keep for back-compat (we’ll auto-fill it from first selected)
  const [, setCourseId] = useState("");

  // partner search
  const [qPartner, setQPartner] = useState("");
  const partnerParams = useMemo(
    () => ({ search: qPartner || undefined }),
    [qPartner],
  );

  const { data: partnersRes, isLoading: loadingPartners } =
    usePartners(partnerParams);

  const partners = useMemo(() => {
    const raw = partnersRes?.data?.documents ?? partnersRes?.data ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [partnersRes]);
  // const partners = partnersRes?.data?.documents || partnersRes?.data || [];

  const { data: membershipRes } = useQuery({
    queryKey: ["membership-types", "all"],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/membership-types", {
        params: { limit: 1000 },
      });
      return data;
    },
  });
  const membershipTypes =
    membershipRes?.data?.documents || membershipRes?.data || [];

  const { data: coursesRes, isLoading: loadingCourses } = useQuery({
    queryKey: ["courses", "all"],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/course", {
        params: { limit: 1000 },
      });
      return data;
    },
  });
  const courses =
    coursesRes?.responseObject?.response?.docs || coursesRes?.data || [];

  // map to MultiSelect options
  const courseOptions =
    !loadingCourses &&
    (courses || []).map((course) => ({
      value: course._id,
      label: course.title,
    }));

  const mutation = useBulkEnrollMembers();

  useEffect(() => {
    if (partners?.length && !partnerId) setPartnerId(partners[0]._id);
  }, [partners, partnerId]);

  // MultiSelect expects onChange({ target: { name, value } })
  const onTextChange = (e) => {
    const { name, value } = e.target || {};
    if (name === "courseIds") {
      setCourseIds(Array.isArray(value) ? value : []);
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!partnerId || !membershipTypeId || courseIds.length === 0 || !file) {
      toast.error(
        "Please select partner, membership, at least one course, and upload an Excel file.",
      );
      return;
    }

    try {
      const first = courseIds[0] || "";
      setCourseId(first);
      await mutation.mutateAsync({
        partnerId,
        membershipTypeId,
        courseId: first,
        courseIds,
        file,
      });

      toast.info(
        "Bulk enrollment started. Check partner members list shortly.",
      );
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to enroll";
      toast.error(msg);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-lg">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Bulk Enroll Members</h3>
          <button onClick={onClose} className="text-slate-500">
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="p-4 space-y-5">
          {/* Partner + search */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-xs mb-1">Search Partner</label>
              <input
                value={qPartner}
                onChange={(e) => setQPartner(e.target.value)}
                placeholder="Filter partners…"
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs mb-1">Partner</label>
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                disabled={loadingPartners}
              >
                <option value="">Select partner</option>
                {partners.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.partnerName || p.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Membership */}
          <div>
            <label className="block text-xs mb-1">Membership Type</label>
            <select
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={membershipTypeId}
              onChange={(e) => setMembershipTypeId(e.target.value)}
            >
              <option value="">Select membership</option>
              {membershipTypes.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.title} ({m.code})
                </option>
              ))}
            </select>
          </div>

          {/* Courses: MultiSelect */}
          <div>
            <MultiSelect
              label={`Select Course(s) ${
                courseIds.length ? `— ${courseIds.length} selected` : ""
              }`}
              name="courseIds"
              value={courseIds}
              onChange={onTextChange}
              options={courseOptions || []}
            />
            {loadingCourses && (
              <div className="mt-1 text-xs text-slate-500">
                Loading courses…
              </div>
            )}
          </div>

          {/* File */}
          <div>
            <label className="block text-xs mb-1">
              Upload Excel (.xlsx or .xls) — columns: <code>firstname</code>,{" "}
              <code>lastname</code>, <code>email</code>
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border file:bg-white file:px-3 file:py-2 file:text-sm"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-3 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              aria-busy={mutation.isPending}
              className="rounded-md bg-black px-3 py-2 text-sm text-white flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {mutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              <span>
                {mutation.isPending ? "Uploading…" : "Start Enrollment"}
              </span>
            </button>
          </div>

          {/* hidden back-compat field */}
          {/* <input type="hidden" name="courseId" value={courseId} readOnly /> */}
        </form>
      </div>
    </div>
  );
}
